import type { InvitationPayload } from '@Application/Security/Invitations/Actions/invitationFetched';
import type NewPasswordFormData from '@UI/Pages/Authentication/Shared/FormData/NewPasswordFormData';
import { css } from '@emotion/react';
import * as React from 'react';
import { Button, Form, Password } from '@verclam/ui-kit';
import { fieldMarginStyles } from '@Guidelines/Theme/commonStyles';

const summaryStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
	margin: 0 0 1.25rem;
`;

/** Joins organization names in French: "A", "A et B", or "A, B et C" — never an Oxford comma before "et". */
function formatOrganizationNames(names: readonly string[]): string {
	if (names.length === 1) {
		return names[0] ?? '';
	}

	const lastName = names[names.length - 1] ?? '';
	return `${names.slice(0, -1).join(', ')} et ${lastName}`;
}

type Props = {
	readonly invitation: InvitationPayload;
	readonly formData: NewPasswordFormData;
	readonly onPasswordChange: (value: string) => void;
	readonly onPasswordConfirmationChange: (value: string) => void;
	readonly onSubmit: () => void;
	readonly isSubmitting: boolean;
};

/** Role and facilities are read-only reminders: the invited visitor never edits them here. */
const InvitationDetails: React.FC<Props> = ({
	invitation,
	formData,
	onPasswordChange,
	onPasswordConfirmationChange,
	onSubmit,
	isSubmitting,
}) => {
	const facilities = invitation.facilityNames.length > 0 ? invitation.facilityNames.join(', ') : '—';
	const organizations = formatOrganizationNames(invitation.organizationNames);

	return (
		<React.Fragment>
			<p>
				Vous êtes invité(e) à rejoindre <strong>{organizations}</strong>.
			</p>
			<dl css={summaryStyle}>
				<dt>Rôle</dt>
				<dd>{invitation.roleLabel ?? '—'}</dd>
				<dt>Agences</dt>
				<dd>{facilities}</dd>
			</dl>
			<Form onSubmit={onSubmit} aria-label="Définir le mot de passe du compte">
				<Password
					name="password"
					label="Mot de passe"
					subLabel="12 caractères minimum."
					feedback={false}
					toggleMask
					autoComplete="new-password"
					required
					value={formData.password}
					onChange={onPasswordChange}
					errorMessage={formData.getValidationMessage('password')}
					isFormFieldValid={formData.isValid('password')}
				/>
				<Password
					name="passwordConfirmation"
					label="Confirmation du mot de passe"
					feedback={false}
					toggleMask
					autoComplete="new-password"
					required
					value={formData.passwordConfirmation}
					onChange={onPasswordConfirmationChange}
					errorMessage={formData.getValidationMessage('passwordConfirmation')}
					isFormFieldValid={formData.isValid('passwordConfirmation')}
				/>
				<Button
					type="submit"
					label="Définir mon mot de passe"
					loading={isSubmitting}
					css={fieldMarginStyles}
				/>
			</Form>
		</React.Fragment>
	);
};

export default InvitationDetails;
